import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  CheckCircle2, 
  XCircle,
  Save,
  Maximize2,
  Minimize2,
  Sun,
  Moon,
  Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface AttendanceRecord {
  id: string;
  member_id: string;
  member_name: string;
  member_title?: string;
  is_present: boolean;
}

export default function MeetingAttendance() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentOrganization } = useAppStore();
  
  const [meeting, setMeeting] = useState<{ id: string; meeting_date: string; meeting_type: string; notes?: string } | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [filteredAttendance, setFilteredAttendance] = useState<AttendanceRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isMeetingMode, setIsMeetingMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');
  const isFuture = meeting ? meeting.meeting_date > today : false;

  useEffect(() => {
    if (meetingId) {
      fetchMeetingAndAttendance();
    }
  }, [meetingId]);

  useEffect(() => {
    const filtered = attendance.filter((record) =>
      record.member_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredAttendance(filtered);
  }, [searchQuery, attendance]);

  const fetchMeetingAndAttendance = async () => {
    if (!meetingId || !currentOrganization) return;

    setIsLoading(true);
    try {
      // Fetch meeting details
      const { data: meetingData, error: meetingError } = await supabase
        .from('meetings')
        .select('*')
        .eq('id', meetingId)
        .single();

      if (meetingError) throw meetingError;
      setMeeting(meetingData);

      // Fetch attendance with member details
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance')
        .select(`
          id,
          member_id,
          is_present,
          member:members(full_name, title)
        `)
        .eq('meeting_id', meetingId);

      if (attendanceError) throw attendanceError;

      const formattedAttendance = attendanceData?.map((a) => ({
        id: a.id,
        member_id: a.member_id,
        member_name: (a.member as { full_name?: string; title?: string } | null)?.full_name || 'Unknown',
        member_title: (a.member as { full_name?: string; title?: string } | null)?.title,
        is_present: a.is_present,
      })) || [];

      setAttendance(formattedAttendance);
      setFilteredAttendance(formattedAttendance);
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAttendance = (index: number) => {
    if (isFuture) return;
    const newAttendance = [...attendance];
    const recordIndex = attendance.findIndex(
      (a) => a.member_id === filteredAttendance[index].member_id
    );
    if (recordIndex !== -1) {
      newAttendance[recordIndex].is_present = !newAttendance[recordIndex].is_present;
      setAttendance(newAttendance);
    }
  };

  const markAllPresent = () => {
    if (isFuture) return;
    const newAttendance = attendance.map((a) => ({ ...a, is_present: true }));
    setAttendance(newAttendance);
  };

  const markAllAbsent = () => {
    if (isFuture) return;
    const newAttendance = attendance.map((a) => ({ ...a, is_present: false }));
    setAttendance(newAttendance);
  };

  const handleSave = async () => {
    if (isFuture) {
      toast.error('Attendance is not open yet for this meeting.');
      return;
    }
    setIsSaving(true);
    try {
      // FIX #2: Validate attendance records
      if (attendance.length === 0) {
        toast.error('No attendance records to save');
        setIsSaving(false);
        return;
      }

      // Update all attendance records
      const results = await Promise.all(
        attendance.map((record) =>
          supabase
            .from('attendance')
            .update({
              is_present: record.is_present,
              check_in_time: record.is_present ? new Date().toISOString() : null,
            })
            .eq('id', record.id)
        )
      );

      const firstError = results.find((result) => result.error)?.error;
      if (firstError) {
        console.error('Error updating attendance:', firstError);
        throw firstError;
      }

      toast.success(t('attendance.attendanceSaved'));

      // FIX #2: Auto-navigate after save with feedback delay
      setTimeout(() => {
        navigate('/attendance');
      }, 1500);
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save attendance records');
    } finally {
      setIsSaving(false);
    }
  };

  const presentCount = attendance.filter((a) => a.is_present).length;
  const attendanceRate = attendance.length > 0 
    ? Math.round((presentCount / attendance.length) * 100) 
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Meeting not found</p>
        <Button onClick={() => navigate('/attendance')} className="mt-4">
          Back to Attendance
        </Button>
      </div>
    );
  }

  if (isFuture) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold">Attendance Not Yet Open</h2>
            <p className="text-muted-foreground">
              Attendance for this meeting opens on{' '}
              <span className="font-medium text-foreground">
                {format(new Date(meeting.meeting_date), 'MMMM dd, yyyy')}
              </span>
              .
            </p>
            <Button onClick={() => navigate('/attendance')} className="mt-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Meetings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Meeting Mode UI
  if (isMeetingMode) {
    return (
      <div className={cn(
        "fixed inset-0 z-50 overflow-auto",
        isDarkMode ? "bg-gray-900" : "bg-background"
      )}>
        {/* Meeting Mode Header */}
        <div className={cn(
          "sticky top-0 z-10 p-4 border-b flex items-center justify-between",
          isDarkMode ? "bg-gray-900 border-gray-800" : "bg-background"
        )}>
          <div>
            <h1 className={cn(
              "text-2xl font-bold",
              isDarkMode && "text-white"
            )}>
              {meeting.meeting_type} Meeting
            </h1>
            <p className={cn(
              "text-muted-foreground",
              isDarkMode && "text-gray-400"
            )}>
              {format(new Date(meeting.meeting_date), 'MMMM dd, yyyy')} • {presentCount}/{attendance.length} present
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsDarkMode(!isDarkMode)}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsMeetingMode(false)}
            >
              <Minimize2 className="w-4 h-4 mr-2" />
              Exit Meeting Mode
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="lg"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* Large Attendance Grid */}
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {attendance.map((record, index) => (
              <button
                key={record.member_id}
                onClick={() => toggleAttendance(index)}
                className={cn(
                  "p-4 rounded-xl text-left transition-all min-h-[100px] flex flex-col justify-between",
                  record.is_present 
                    ? "bg-green-500 text-white" 
                    : isDarkMode ? "bg-gray-800 text-gray-300" : "bg-muted text-foreground",
                  "hover:scale-[1.02] active:scale-[0.98]"
                )}
              >
                <div>
                  <p className="font-bold text-lg leading-tight">{record.member_name}</p>
                  {record.member_title && (
                    <p className={cn(
                      "text-sm opacity-80",
                      record.is_present ? "text-white" : "text-muted-foreground"
                    )}>
                      {record.member_title}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  {record.is_present ? (
                    <CheckCircle2 className="w-8 h-8" />
                  ) : (
                    <XCircle className={cn("w-8 h-8", isDarkMode ? "text-gray-600" : "text-muted-foreground")} />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate('/attendance')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">
            {meeting.meeting_type} Meeting
          </h1>
          <p className="text-muted-foreground">
            {format(new Date(meeting.meeting_date), 'MMMM dd, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsMeetingMode(true)}
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            {t('attendance.meetingMode')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t('attendance.saveAttendance')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{attendance.length}</p>
            <p className="text-sm text-muted-foreground">{t('attendance.totalMembers')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{presentCount}</p>
            <p className="text-sm text-muted-foreground">{t('attendance.presentCount')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{attendanceRate}%</p>
            <p className="text-sm text-muted-foreground">{t('attendance.attendanceRate')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('members.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={markAllPresent}>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            All Present
          </Button>
          <Button variant="outline" onClick={markAllAbsent}>
            <XCircle className="w-4 h-4 mr-2" />
            All Absent
          </Button>
        </div>
      </div>

      {/* Attendance List */}
      <div className="space-y-2">
        {filteredAttendance.map((record, index) => (
          <Card 
            key={record.member_id}
            className={cn(
              "transition-colors",
              record.is_present && "border-green-500/50 bg-green-50"
            )}
          >
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center",
                  record.is_present ? "bg-green-100" : "bg-muted"
                )}>
                  <Users className={cn(
                    "w-5 h-5",
                    record.is_present ? "text-green-600" : "text-muted-foreground"
                  )} />
                </div>
                <div>
                  <p className="font-medium">{record.member_name}</p>
                  {record.member_title && (
                    <p className="text-sm text-muted-foreground">{record.member_title}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={record.is_present ? 'default' : 'secondary'}>
                  {record.is_present ? t('attendance.present') : t('attendance.absent')}
                </Badge>
                <Switch
                  checked={record.is_present}
                  onCheckedChange={() => toggleAttendance(index)}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAttendance.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No members found</p>
        </div>
      )}
    </div>
  );
}
